<template>
  <WizardDialog
    ref="dialog"
    title="Onboard Account"
    icon="fa-user-plus"
    sizeClass="modal-xl modal-h60"
    :form="form"
    :wizardTabList="wizardTabList"
    :loading="loading"
    :loadingError="loadingError"
    :submitting="submitting"
    submitTitle="Create"
    @closed="onClosed"
    @submitted="onSubmitted">

    <template v-slot:content>
      <FormWizardStep stepid="account">
        <ContentTitle>Account</ContentTitle>
        <FormRow label="Company Name"><FormInputText v-model="account.companyName" field="companyName" placeholder="Company Name" /></FormRow>

        <FormRow label="Timezone Code">
          <FormSearchableSelect :options="timezoneCodeList" placeholder="Timezone" v-model="account.timezoneCode" field="timezoneCode" />
        </FormRow>
        <FormRow label="Country Code">
          <FormSelect v-model="account.countryCode" :options="countryCodeLookup" field="countryCode" />
        </FormRow>
        <FormRow label="State Code">
          <FormSelect v-model="account.stateCode" :options="stateCodeLookup" field="stateCode" />
        </FormRow>

        <FormHorizontalRule />

        <ContentTitle>Owner User</ContentTitle>
        <FormRow label="Name"><FormInputText v-model="account.userName" field="userName" placeholder="User Name" /></FormRow>
        <FormRow label="Email"><FormInputText v-model="account.email" field="email" placeholder="Email" note="This will be the owner user login email address" /></FormRow>
      </FormWizardStep>

      <FormWizardStep stepid="company">
        <ContentTitle>Company Address</ContentTitle>
        <FormRow label="Street Address">
          <FormInputAddressSearch
            placeholder="Address Line 1"
            v-model="account.address1"
            field="address1"
            @updateAddress2="account.address2 = $event"
            @updateSuburb="account.suburb = $event"
            @updatePostcode="account.postcode = $event"
            @updateState="account.state = $event"
            @updateCountry="account.country = $event"
          />
        </FormRow>
        <FormRow label=" "><FormInputText placeholder="Address Line 2" v-model="account.address2" field="address2" /></FormRow>
        <FormRow label="Suburb"><FormInputText placeholder="Suburb" v-model="account.suburb" field="suburb" /></FormRow>
        <FormRow label="Postcode"><FormInputText placeholder="Postcode" v-model="account.postcode" field="postcode" /></FormRow>
        <FormRow label="State"><FormInputText placeholder="State" v-model="account.state" field="state" /></FormRow>
        <FormRow label="Country"><FormInputText placeholder="Country" v-model="account.country" field="country" /></FormRow>

        <FormHorizontalRule />

        <ContentTitle>Company Contact Info</ContentTitle>
        <FormRow label="Email">
          <FormInputText placeholder="Email" v-model="account.contactEmail" field="contactEmail" />
        </FormRow>
        <FormRow label="Phone">
          <FormInputText placeholder="Phone" v-model="account.contactPhone" field="contactPhone" />
        </FormRow>
        <FormRow label="Fax">
          <FormInputText placeholder="Fax" v-model="account.contactFax" field="contactFax" />
        </FormRow>
        <FormRow label="Website">
          <FormInputText placeholder="Website" v-model="account.contactWeb" field="contactWeb" />
        </FormRow>
      </FormWizardStep>

      <FormWizardStep stepid="subscription">
        <ContentTitle>Initial Subscription</ContentTitle>
        <FormRow label="Trial Mode Active">
          <FormCheckbox v-model="account.isTrialActive" field="isTrialActive" />
        </FormRow>
        <FormRow label="Trial Expiry Date">
          <FormDate v-model="account.expiryDate" field="expiryDate" />
        </FormRow>
        <FormRow label="Subscription">
          <FormSelect v-model="account.billingProductId" field="billingProductId" :options="productLookup" />
        </FormRow>
        <FormRow label="Monthly Fee">
          <FormInputCurrency v-model="account.monthlyFee" field="monthlyFee" note="Monthly fee (inc GST) once trial expires" />
        </FormRow>
      </FormWizardStep>
    </template>
  </WizardDialog>
</template>

<script>
import _ from 'lodash'
import moment from 'moment-timezone'

import form from '@/mixins/form'
import modalDialog from '@/mixins/modalDialog'
import WizardDialog from '@/components/dialog/wizardDialog.vue'

export default {
  name: 'OnboardAccountDialog',
  mixins: [
    modalDialog,
    form
  ],
  components: {
    WizardDialog
  },
  props: {
  },
  data() {
    return {
      account: {
        companyName: '',
        userName: '',
        email: '',

        address1: '',
        address2: '',
        suburb: '',
        state: '',
        postcode: '',
        country: 'Australia',

        contactEmail: '',
        contactPhone: '',
        contactFax: '',
        contactWeb: '',

        timezoneCode: 'Australia/Adelaide',
        countryCode: 'AU',
        stateCode: 'SA',

        isTrialActive: false,
        expiryDate: '',
        billingProductId: null,
        monthlyFee: 0
      },
      form: {},
      billingProductList: [],
      submitting: false,
      loading: false,
      loadingError: '',

      timezoneCodeList: [],

      // Fixed just to Australia states for now
      countryCodeLookup: [
        { value: 'AU', name: 'Australia' },
      ],
      stateCodeLookup: [
        { value: 'ACT', name: 'Australian Capital Territory' },
        { value: 'NSW', name: 'New South Wales' },
        { value: 'NT', name: 'Northern Territory' },
        { value: 'QLD', name: 'Queensland' },
        { value: 'SA', name: 'South Australia' },
        { value: 'TAS', name: 'Tasmania' },
        { value: 'VIC', name: 'Victoria' },
        { value: 'WA', name: 'Western Australia' }
      ],

      wizardTabList: [{ title: 'Account', id: 'account' }, { title: 'Company Details', id: 'company' }, { title: 'Subscription', id: 'subscription' }],
    }
  },
  mounted() {
    this.timezoneCodeList = moment.tz.names().map(tz => { return { name: tz, value: tz } })    
  },
  computed: {
    productLookup() {
      return _.concat(
        [{ value: 0, name: 'None' }]
      , this.billingProductList
      .filter(billingProduct => {
        if(!billingProduct.isActive)
          return false

        // Only use products that have subscription
        if(billingProduct.isAddonProduct)
          return false

        return true
      })
      .map(billingProduct => {
        return {
          value: billingProduct.id,
          name: `${billingProduct.name} - ${billingProduct.description}`
        }
      }))
    }
  },
  methods: {
    show(params) {
      // Reset form
      this.form = {}
      this.submitting = false

      this.requestData()

      return this.$refs.dialog.show(params)
    },
    onClosed() {
      this.$refs.dialog.close(false)
    },
    onSubmitted() {
      this.form = this.clearFormResponse()

      this.confirmMessage('Create Account', 'Are you sure you want to create the account?')
      .then(confirm => {
        if(confirm) {
          this.submitting = true

          this.$apiService.developerService.developerOnboardNewAccount(this.account)
            .then(response => {
              this.submitting = false
              this.$refs.dialog.close(true)

              this.successToast('Account Created')
              
              // Redirect to the account page
              this.$router.push(`/developer/accounts/${response.data.id}`)
            })
            .catch(err => {
              this.submitting = false
              this.$data.form = this.processFormResponse(err)
              this.$refs.dialog.displayErrors()
            })
        }
      })
    },
    requestData() {
      this.loading = true
      this.$apiService.developerService.developerGetBillingProducts()
      .then(response => {
        this.billingProductList = response.data
      })
      .finally(() => { 
        this.loading = false
      })
    }
  }
}
</script>